import { useMemo } from 'react';

import { Avatar } from '@components/avatar';
import { Button } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { IconTeam } from '@components/icons/team';
import { Spinner } from '@components/loading-spinner';
import { hideSidebar, Sidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { compareStrings } from '@utils/sorting';

import { useActiveWorkspaceUsers } from 'api/users/hooks';
import { useCurrentWorkspace } from 'ui/authenticated/main/helpers';

export const useManualSidebar = () => {
  const sidebar = useSidebar(ManualSidebar, {
    variant: 'REGULAR',
  });
  return sidebar;
};

export const ManualSidebar = () => {
  const { t } = useScopedTranslate(locale);
  const { shortId: workspaceId } = useCurrentWorkspace();
  const { data: users = [], isLoading: isUsersLoading } = useActiveWorkspaceUsers(workspaceId);

  const sortedUsers = useMemo(
    () => [...users].sort((a, b) => compareStrings(a.name, b.name)),
    [users],
  );

  if (workspaceId && isUsersLoading) {
    return <Spinner />;
  }

  return (
    <Sidebar>
      <Sidebar.SimpleHeader icon={IconTeam} iconColor="elementTeal100" title={t('manualAdd')} />
      <Sidebar.Content>
        <Stack space={6}>
          <Stack space={4}>
            <Text font="regular14" text={t('manualAlwaysPossible')} />
            <Text font="regular14" text={t('configureJourneyFields')} />
          </Stack>
          <Sidebar.Separator />
          <Text font="regular14" text={t('usersCanAddToJourney')} />
          <Stack space={2}>
            <Text font="semibold14" text={t('usersInWorkspace')} />
            {sortedUsers.map((user) => {
              return (
                <Stack key={user.shortId} orientation="horizontal" space={2}>
                  <Avatar name={user.name} src={user.picture} color="purple" />
                  <Text font="regular14" text={user.name} />
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      </Sidebar.Content>
      <Sidebar.Footer>
        <ButtonGroup align="right">
          <Button text={t('core.close')} variant="secondary" onClick={hideSidebar} />
        </ButtonGroup>
      </Sidebar.Footer>
    </Sidebar>
  );
};

export const locale = {
  en: {
    manualAdd: 'Manual add',
    manualAlwaysPossible:
      'Manually add any traveller to this journey. Adding by this method is always possible.',
    configureJourneyFields: '',
    usersCanAddToJourney:
      'Any user with access to this workspace can add travellers to the journey',
    usersInWorkspace: 'Users in workspace',
  },
};
